/**
 * One lot: its cattle and photos, what stands between it and the sale, its own terms, the buyers
 * around it, and (for an auction) the bid ledger and the clerk's entry.
 */
import { useRef, useState } from "react";
import { Link, useParams } from "react-router";
import type { EventDTO, Money } from "../../../contract";
import { api, request } from "../../../web/lib/api";
import { formatDate, formatMoney, relativeDay } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useMe } from "../../../web/lib/session";
import { RefPicker } from "../../../web/forms/pickers";
import { SchemaForm, issuesToErrors, type Errors, type Values } from "../../../web/forms/SchemaForm";
import { ApiError } from "../../../web/lib/api";
import { Page } from "../../../web/shell/Layout";
import { useCaptureSubject } from "../../../web/shell/Composer";
import { EventRow } from "../../../web/shell/Timeline";
import { classLabel } from "../../../web/shell/describe";
import { Button, Card, Chip, Disclosure, ErrorNote, Field, Icon, IconButton, List, Loading, Menu, PageHeader, Row, Section, Select, Sheet, TagLabel, TextInput, toast } from "../../../web/ui/kit";
import { LOT_EDIT_FIELDS } from "../contract";
import type { AuctionLotState, BidReceipt, LotDTO, OfferingKind } from "../shared/types";
import { BID_REASON_LABELS as REASONS } from "../shared/labels";
import { act, LotNumber, LotStatusChip, patch, post, priceText, ReadinessPill, recordSale, tm, useToday } from "./common";

interface LotDetail {
  lot: LotDTO;
  offering: { id: string; title: string; kind: OfferingKind; sale_date: string | null; status: string; auction: { mode: "timed" | "live" } | null };
  events: EventDTO[];
  auction: AuctionLotState | null;
  can: { manage: boolean; record: boolean; finance_write: boolean };
}

export function LotPage() {
  useLanguage();
  const { lotId } = useParams();
  const q = useApi<LotDetail>(`/m/sales/lots/${lotId}`);
  useCaptureSubject(q.data ? { job_id: q.data.offering.id, subject: { kind: "job", id: q.data.offering.id, label: q.data.offering.title } } : null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState<string[] | null>(null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const { lot, offering, events, auction, can } = q.data;
  const ref = { id: offering.id, label: offering.title };
  const lots = [lot.lot_number];
  const closed = ["withdrawn", "no_sale"].includes(lot.status);
  const open = !closed && !["sold", "delivered"].includes(lot.status);

  const primary = (() => {
    if (lot.status === "sold") return <Button variant="primary" size="lg" icon="truck" onClick={() => recordSale("sales.delivery", { offering: ref, lots, animals: lot.animals, buyer: lot.buyer })}>{t("Record delivery")}</Button>;
    if (lot.status === "delivered") return can.manage && lot.animals.length ? <Button variant="primary" size="lg" icon="packet" to={`/work/sales/packets/new?animals=${lot.animals.map((a) => a.id).join(",")}${lot.buyer ? `&buyer=${lot.buyer.id}` : ""}&offering=${offering.id}`}>{t("Share records with the buyer")}</Button> : null;
    if (!open || offering.auction) return null;
    return can.manage ? <Button variant="primary" size="lg" icon="handshake" onClick={() => recordSale("sales.agreement", { offering: ref, lots, animals: lot.animals, buyer: lot.reservation?.buyer ?? null, data: { price_basis: "total", ...(lot.asking ? { price: lot.asking } : {}) } })}>{t("Record the sale")}</Button> : null;
  })();

  const menu = can.manage ? [
    ...(lot.stage === "draft" && open ? [{ label: t("Mark ready"), icon: "check", onSelect: () => void setStage(lot, "ready") }] : []),
    ...(lot.stage !== "published" && open ? [{ label: t("Publish the listing"), icon: "catalog", onSelect: () => void setStage(lot, "published") }] : []),
    ...(lot.stage !== "draft" && open ? [{ label: t("Back to draft"), icon: "edit", onSelect: () => void setStage(lot, "draft") }] : []),
    { label: t("Edit lot"), icon: "edit", onSelect: () => setEditing(true) },
    "divider" as const,
    ...(open ? [{ label: t("No sale"), icon: "minus", onSelect: () => void act(() => post(`/lots/${lot.id}/no-sale`, {}), t("Marked no sale")) }, { label: t("Pull from the sale"), icon: "archive", danger: true, onSelect: () => void act(() => post(`/lots/${lot.id}/withdraw`, {}), t("Pulled from the sale")) }] : []),
    ...(closed ? [{ label: t("Put back in the sale"), icon: "reset", onSelect: () => void act(() => post(`/lots/${lot.id}/reopen`, {}), t("Back in the sale")) }] : []),
  ] : [];

  return (
    <Page>
      <PageHeader back={{ to: `/work/jobs/${offering.id}?tab=lots`, label: offering.title }} eyebrow={t("Lot")}
        title={<span className="sales-inline"><LotNumber n={lot.lot_number} size="lg" />{lot.title ?? ""}</span>}
        subtitle={[lot.buyer ? lot.buyer.label : null, lot.agreement?.price ? priceText(lot.agreement.price, lot.agreement.price_basis) : lot.asking ? t("asking {{price}}", { price: priceText(lot.asking, lot.price_basis) }) : null].filter(Boolean).join(" · ")}
        actions={menu.length ? <Menu label={t("Lot options")} items={menu} /> : undefined}>
        <div className="animal-status"><LotStatusChip status={lot.status} />{open && <ReadinessPill r={lot.readiness} />}{lot.stage === "published" && open && <Chip tone="info" icon="catalog">{t("Published")}</Chip>}</div>
      </PageHeader>
      {(primary || (open && can.record && !offering.auction)) && <div className="quick-actions">{primary}
        {open && can.record && !offering.auction && <Button size="lg" icon="contact" onClick={() => recordSale("sales.inquiry", { offering: ref, lots, animals: lot.animals })}>{t("A buyer called")}</Button>}
        {open && can.record && !offering.auction && <Button size="lg" icon="coins" onClick={() => recordSale("sales.offer", { offering: ref, lots, animals: lot.animals })}>{t("A buyer made an offer")}</Button>}
        {open && !lot.reservation && can.manage && !offering.auction && <Button size="lg" icon="clock" onClick={() => recordSale("sales.reservation", { offering: ref, lots, animals: lot.animals, data: { status: "held" } })}>{t("Hold for a buyer")}</Button>}
      </div>}

      {lot.reservation && open && (
        <Card className="sales-next">
          <p className="sales-next-title">{lot.reservation.until ? t("Held for {{buyer}} until {{date}}", { buyer: lot.reservation.buyer.label, date: formatDate(lot.reservation.until) }) : t("Held for {{buyer}}", { buyer: lot.reservation.buyer.label })}</p>
          {lot.reservation.deposit && <p className="muted">{t("{{amount}} down", { amount: formatMoney(lot.reservation.deposit) })}</p>}
          {can.manage && <Button variant="ghost" onClick={() => recordSale("sales.reservation", { offering: ref, lots, buyer: lot.reservation!.buyer, data: { status: "released" } })}>{t("Release the hold")}</Button>}
        </Card>
      )}

      {auction && <AuctionLot lot={lot} auction={auction} live={offering.auction?.mode === "live"} can={can} refresh={() => void q.refetch()} />}

      <Section title={t("Cattle")} count={lot.animals.length || undefined} action={can.manage && !["sold", "delivered"].includes(lot.status) ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding([])}>{t("Add")}</Button> : undefined}>
        {lot.animals.length === 0 ? <p className="muted">{lot.head_count ? plural(lot.head_count, "{{count}} head, not tagged", "{{count}} head, not tagged") : t("No cattle in this lot.")}</p> : (
          <ul className="sales-lot-animals">
            {lot.animals.map((a) => (
              <li key={a.id}>
                <Link to={`/cattle/${a.id}`} className="sales-lot-animal">
                  {a.tag ? <TagLabel tag={a.tag} /> : <strong>{a.name}</strong>}{a.tag && a.name && <span>{a.name}</span>}
                  <span className="muted">{[classLabel(a.class), a.birth_date ? t("born {{date}}", { date: formatDate(a.birth_date) }) : null].filter(Boolean).join(" · ")}</span>
                </Link>
                {can.manage && !["sold", "delivered"].includes(lot.status) && <IconButton icon="delete" label={t("Take {{animal}} out of this lot", { animal: a.label })} onClick={() => void act(() => post(`/lots/${lot.id}/animals`, { remove: [a.id] }), t("Taken out of the lot"))} />}
              </li>
            ))}
          </ul>
        )}
        {adding && (
          <Card>
            <Field label={t("Add cattle")}><RefPicker target="animal" multiple value={adding} onChange={(v) => setAdding((v as string[]) ?? [])} filter={{ status: "current" }} autoFocus /></Field>
            <div className="row-actions"><Button variant="primary" disabled={!adding.length} onClick={() => void act(() => post(`/lots/${lot.id}/animals`, { add: adding }), t("Added to the lot")).then((ok) => ok && setAdding(null))}>{t("Add to lot {{lot}}", { lot: lot.lot_number })}</Button><Button variant="ghost" onClick={() => setAdding(null)}>{t("Cancel")}</Button></div>
          </Card>
        )}
      </Section>

      {lot.animals.length > 0 && <Photos lot={lot} can={can.record} />}

      {open && lot.checks.length > 0 && (
        <Section title={t("Before the sale")} action={<ReadinessPill r={lot.readiness} />}>
          <List>{lot.checks.filter((c) => c.status === "block" || c.status === "warn").map((c, i) => <Row key={i} icon={c.status === "block" ? "warn" : "info"} tone={c.status === "block" ? "bad" : "warn"} title={tm(c.label)} to={c.link} />)}</List>
          <Disclosure label={t("What's already in order")} count={lot.checks.filter((c) => c.status === "ok").length}>
            <ul className="sales-checks">{lot.checks.filter((c) => c.status === "ok" || c.status === "unknown").map((c, i) => <li key={i} className={c.status === "unknown" ? "muted" : undefined}><Icon name={c.status === "ok" ? "check" : "help"} size={14} />{tm(c.label)}</li>)}</ul>
          </Disclosure>
        </Section>
      )}

      <Section title={t("For the catalog")} action={can.manage ? <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(true)}>{t("Edit")}</Button> : undefined}>
        <Card>
          <dl className="sales-kvs">
            {lot.footnote ? <div className="sales-kv"><dt>{t("Footnote")}</dt><dd className="sales-prose">{lot.footnote}</dd></div> : <p className="muted">{t("No footnote yet.")}</p>}
            {lot.retained && <div className="sales-kv"><dt>{t("Retained interests")}</dt><dd>{lot.retained}</dd></div>}
            {lot.asking && <div className="sales-kv"><dt>{t("Asking price")}</dt><dd>{priceText(lot.asking, lot.price_basis)}</dd></div>}
            {lot.reserve && can.manage && <div className="sales-kv"><dt>{t("Reserve")}</dt><dd>{formatMoney(lot.reserve)} <span className="muted">{t("Private")}</span></dd></div>}
          </dl>
        </Card>
      </Section>

      <Section title={t("History")} count={events.length || undefined}>
        {events.length ? <ol className="timeline-list">{events.map((e) => <EventRow key={e.id} event={e} />)}</ol> : <p className="muted">{t("Nothing recorded for this lot yet.")}</p>}
      </Section>

      {editing && <EditLotSheet lot={lot} onClose={() => setEditing(false)} />}
    </Page>
  );
}

async function setStage(lot: LotDTO, stage: "draft" | "ready" | "published") {
  try {
    await patch(`/lots/${lot.id}`, { stage });
    await invalidateAll();
    toast(t("Saved"), { tone: "good" });
  } catch (err) {
    if (err instanceof ApiError && err.code === "lot_blocked") {
      if (confirm(t("Something on this lot blocks the sale. Mark it anyway?"))) await act(() => patch(`/lots/${lot.id}`, { stage, force: true }), t("Saved"));
    } else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
  }
}

function EditLotSheet({ lot, onClose }: { lot: LotDTO; onClose: () => void }) {
  const [value, setValue] = useState<Values>({
    lot_number: lot.lot_number, title: lot.title ?? undefined, asking: lot.asking ?? undefined, footnote: lot.footnote ?? undefined, retained: lot.retained ?? undefined,
    reserve: lot.reserve ?? undefined, price_basis: lot.price_basis ?? undefined, kind: lot.kind, head_count: lot.head_count ?? undefined,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await patch(`/lots/${lot.id}`, Object.fromEntries(Object.keys(LOT_EDIT_FIELDS).map((k) => [k, value[k] ?? null])));
      await invalidateAll();
      toast(t("Saved"), { tone: "good" });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues, ""));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Edit lot {{lot}}", { lot: lot.lot_number })}>
      <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <SchemaForm fields={LOT_EDIT_FIELDS} value={value} onChange={setValue} errors={errors} />
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} icon="check">{t("Save")}</Button><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button></div>
      </form>
    </Sheet>
  );
}

// ---------------------------------------------------------------- photos

function Photos({ lot, can }: { lot: LotDTO; can: boolean }) {
  const [animal, setAnimal] = useState(lot.animals[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const photos = useApi<{ items: Array<{ id: string; mime: string; name: string | null; url: string }> }>(animal ? `/m/sales/animals/${animal}/photos` : null);
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of [...files]) await request("POST", `/m/sales/animals/${animal}/photos?name=${encodeURIComponent(file.name)}`, file, { headers: { "content-type": file.type || "application/octet-stream" } });
      await invalidateAll();
      toast(plural(files.length, "{{count}} photo added", "{{count}} photos added"), { tone: "good" });
    } catch (err) {
      toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  return (
    <Section title={t("Photos and video")} className="sales-photos-section">
      <span id="photos" />
      {lot.animals.length > 1 && (
        <Select aria-label={t("Animal")} value={animal} onChange={(e) => setAnimal(e.target.value)}>{lot.animals.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</Select>
      )}
      {photos.data?.items.length ? (
        <div className="sales-photos">
          {photos.data.items.map((p) => (
            <figure key={p.id}>
              {p.mime.startsWith("video/") ? <video src={p.url} controls preload="metadata" /> : <img src={p.url} alt={p.name ?? t("Photo")} loading="lazy" />}
              {can && <IconButton icon="delete" label={t("Remove photo")} onClick={() => void act(() => api.del(`/m/sales/photos/${p.id}`), t("Photo removed"))} />}
            </figure>
          ))}
        </div>
      ) : <p className="muted">{t("No photos yet. Buyers look at the picture first.")}</p>}
      {can && (
        <>
          <input ref={input} type="file" accept="image/*,video/*" capture="environment" multiple hidden onChange={(e) => void upload(e.target.files)} />
          <Button size="lg" icon="camera" busy={busy} onClick={() => input.current?.click()}>{t("Add a photo")}</Button>
        </>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- auction

function AuctionLot({ lot, auction, live, can, refresh }: { lot: LotDTO; auction: AuctionLotState; live: boolean; can: LotDetail["can"]; refresh: () => void }) {
  const today = useToday();
  const { data: me } = useMe();
  const currency = me?.ranch?.currency ?? "USD";
  const offering = useApi<{ bidders: Array<{ id: string; number: string; contact: { label: string }; status: string }> }>(`/m/sales/offerings/${lot.offering_id}`);
  const [bidder, setBidder] = useState("");
  const [amount, setAmount] = useState("");
  const [max, setMax] = useState("");
  const [channel, setChannel] = useState(live ? "floor" : "phone");
  const [last, setLast] = useState<BidReceipt | null>(null);
  const [busy, setBusy] = useState(false);
  const toMinor = (s: string): Money | null => { const n = Number(s.replace(/[,$\s]/g, "")); return s.trim() && Number.isFinite(n) && n > 0 ? { minor: Math.round(n * 100), currency } : null; };
  const place = async () => {
    const a = toMinor(amount);
    if (!bidder || !a) return;
    setBusy(true);
    try {
      const receipt = await post<BidReceipt>(`/lots/${lot.id}/bids`, { bidder_id: bidder, amount: a, max: toMinor(max), channel, key: `bid-${crypto.randomUUID()}` });
      setLast(receipt);
      if (receipt.status === "accepted") { setAmount(""); setMax(""); }
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  const closeLot = async () => {
    if (!confirm(live ? t("Sell lot {{lot}} to the last bid?", { lot: lot.lot_number }) : t("Close bidding on lot {{lot}}?", { lot: lot.lot_number }))) return;
    await act(() => post(`/lots/${lot.id}/close`), t("Lot closed"));
    refresh();
  };
  const bidders = (offering.data?.bidders ?? []).filter((b) => b.status === "approved");
  return (
    <Section title={t("Bidding")}>
      <Card className="sales-auction">
        <div className="sales-auction-price">
          <span className="stat-value">{auction.price ? formatMoney(auction.price) : "—"}</span>
          <span className="stat-label">{auction.leader ? t("Bidder {{number}} leads", { number: auction.leader.number }) : t("No bids yet")}</span>
        </div>
        <p className="muted">{[auction.open ? (auction.closes_at ? t("Closes {{when}}", { when: formatDate(auction.closes_at, "minute") }) : live ? t("Open until the clerk sells it") : null) : t("Bidding is closed"),
          auction.extended ? plural(auction.extended, "Extended {{count}} time", "Extended {{count}} times") : null,
          auction.reserve_met === false ? t("Reserve not met yet") : auction.reserve_met ? t("Reserve met") : null].filter(Boolean).join(" · ")}</p>
        {auction.open && can.record && (
          <form className="sales-bid-form" onSubmit={(e) => { e.preventDefault(); void place(); }}>
            <Field label={t("Bidder")}>
              <Select value={bidder} onChange={(e) => setBidder(e.target.value)} required>
                <option value="">{t("Choose…")}</option>
                {bidders.map((b) => <option key={b.id} value={b.id}>{`${b.number} · ${b.contact.label}`}</option>)}
              </Select>
            </Field>
            <Field label={t("Bid")}><div className="input-prefix"><span className="prefix">$</span><TextInput inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></div></Field>
            {!live && <Field label={t("Up to (private)")} optional hint={t("Hondo bids for them up to this and never shows it.")}><div className="input-prefix"><span className="prefix">$</span><TextInput inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} /></div></Field>}
            <Field label={t("How it came in")}>
              <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="floor">{t("Ring")}</option><option value="phone">{t("Phone")}</option><option value="online">{t("Online")}</option><option value="absentee">{t("Left ahead of time")}</option>
              </Select>
            </Field>
            <Button variant="primary" size="lg" type="submit" busy={busy} disabled={!bidder || !toMinor(amount)} icon="gavel">{t("Enter bid")}</Button>
          </form>
        )}
        {last && (
          <p className={last.status === "accepted" ? "sales-receipt is-ok" : "sales-receipt is-bad"} role="status">
            {last.status === "accepted" ? (last.leading ? t("Taken. They lead at {{price}}.", { price: last.price ? formatMoney(last.price) : "" }) : t("Taken, but another bidder's limit is higher. Price now {{price}}.", { price: last.price ? formatMoney(last.price) : "" })) : t(REASONS[last.reason ?? ""] ?? "Not taken.")}
            {last.duplicate && ` ${t("This bid was already in.")}`}
          </p>
        )}
        {can.manage && auction.open && <Button variant={live ? "primary" : "secondary"} icon="done" onClick={() => void closeLot()}>{live ? t("Sell to the last bid") : t("Close bidding")}</Button>}
      </Card>
      {auction.bids.length > 0 && (
        <Disclosure label={t("All bids")} count={auction.bids.length} defaultOpen={live}>
          <ol className="sales-bids">
            {auction.bids.map((b) => (
              <li key={b.id} className={`is-${b.status}`}>
                <span className="sales-bidder">{b.bidder.number}</span>
                <span className="sales-bid-main"><strong>{formatMoney(b.amount)}</strong><span className="muted">{[b.bidder.label, relativeDay(b.received_at.slice(0, 10), today), new Date(b.received_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), b.has_max ? t("with a private limit") : null].filter(Boolean).join(" · ")}</span>
                  {b.status === "rejected" && <span className="muted">{t(REASONS[b.reason ?? ""] ?? "Not taken.")}</span>}
                  {b.status === "retracted" && <span className="muted">{t("Taken back: {{reason}}", { reason: b.retract_reason ?? "" })}</span>}
                </span>
                {can.manage && b.status === "accepted" && <Button size="sm" variant="ghost" onClick={() => { const reason = prompt(t("Why take this bid back?")); if (reason?.trim()) void act(() => post(`/bids/${b.id}/retract`, { reason }), t("Bid taken back")).then(refresh); }}>{t("Take back")}</Button>}
              </li>
            ))}
          </ol>
        </Disclosure>
      )}
    </Section>
  );
}

